import { isEmpty, isNull, map } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { ADP } from '../../../common/analytics/sourceTypes';
import noImageAvailable from '../../../common/assets/noImageAvailable.svg';
import { formatResource } from '../../../common/helpers/localization/contentResourceHelper';
import { getPostItems } from '../../../common/helpers/ui/dataFilter';
import {
  getPostCategoryInThai,
  PostSubcategory,
} from '../../../common/helpers/domain/postCategories';
import { getContentPagePath } from '../../../common/helpers/routing/urlGenerator';
import contentResource from '../../../locales/th/content.json';
import { trackRelatedArticle } from '../../../common/analytics/analyticsTracker';
import LazyImage from '../../lazyImage/LazyImage';
import { PlayIcon } from '../../playIcon/PlayIcon';
import {
  ArticleImage,
  ArticleInfo,
  DetailWrapper,
  ProjectRelatedArticleDetail,
  ProjectRelatedArticleWrapper,
  SectionTitle,
} from './ProjectRelatedArticle.style';
import { projectRelatedArticleImageConfig } from '../../../common/helpers/sharpie/sharpie';
import { handleImageUrl } from '../../../common/helpers/sharpie/imageUrlHelper';
import {
  isVideoPost,
  VideoType,
} from '../../../common/helpers/domain/videoTypes';
import { getArticleTypeInGA } from '../../../common/analytics/helpers/articleTypeHelper';
import { AnalyticsProject } from '../../../common/analytics/interfaces/analyticsProject';
import { WholeState } from '../../../store/common/type';

interface PostItem {
  id: number;
  title: string;
  propertyType: string[];
  postCategory: PostSubcategory;
  link: string;
  featuredImage: { url: string } | null;
  videoType?: VideoType;
}
interface ProjectRelatedPostData {
  projectArticles?: { items: PostItem[] };
}
interface ProjectRelatedArticleProps {
  projectName: string;
  post: { project: AnalyticsProject };
  lazyLoad?: boolean;
  projectRelatedPostData?: ProjectRelatedPostData;
}
const ProjectRelatedArticle: React.FC<ProjectRelatedArticleProps> = ({
  projectName,
  post,
  lazyLoad,
  projectRelatedPostData,
}) => {
  const isNextGenSupported = useSelector(
    (state: WholeState) => state.common.isNextGenSupported,
  );
  const userInfo = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.user : {},
  );

  if (!isEmpty(projectRelatedPostData)) {
    const { projectArticles = { items: [] } } = projectRelatedPostData;
    const projectRelatedArticle = getPostItems(projectArticles);

    return (
      <>
        {!isEmpty(projectRelatedArticle) && (
          <ProjectRelatedArticleWrapper data-test={'project-related-article'}>
            <SectionTitle>
              {formatResource(
                contentResource.relatedProjectInfo.relatedArticle,
                {
                  project_name: projectName,
                },
              )}
            </SectionTitle>
            {map(projectRelatedArticle, (item, i) => {
              const link = getContentPagePath({
                title: item.title,
                propertyType: item.propertyType,
                id: item.id,
                postCategory: item.postCategory,
                link: item.link,
              });
              const imageUrl = handleImageUrl(
                item.featuredImage.url,
                projectRelatedArticleImageConfig.width,
                projectRelatedArticleImageConfig.height,
                isNextGenSupported,
              );
              const articleImage = !isNull(item.featuredImage) ? (
                <LazyImage
                  src={imageUrl || ''}
                  alt={item.title}
                  lazyLoad={lazyLoad}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{ __html: noImageAvailable }}
                  className="no-image"
                />
              );

              const articleTypeInGA = getArticleTypeInGA(
                item.postCategory,
                item.videoType,
              );

              return (
                <ProjectRelatedArticleDetail key={i}>
                  <a
                    href={link}
                    onClick={() =>
                      trackRelatedArticle(
                        userInfo,
                        {
                          ...item,
                          articleTypeInGA,
                        },
                        post.project,
                        ADP.OTHER_ARTICLE,
                      )
                    }
                  >
                    <DetailWrapper>
                      <ArticleImage className="article-image">
                        {isVideoPost(item.videoType) ? (
                          <div className="img-wrapper">
                            <span className="play-icon-wrapper">
                              <PlayIcon />
                            </span>
                            {articleImage}
                          </div>
                        ) : (
                          articleImage
                        )}
                      </ArticleImage>
                      <ArticleInfo>
                        <p className="article-post-type">
                          {getPostCategoryInThai(item.postCategory)}
                        </p>
                        <p className="article-title">{item.title}</p>
                      </ArticleInfo>
                    </DetailWrapper>
                  </a>
                </ProjectRelatedArticleDetail>
              );
            })}
          </ProjectRelatedArticleWrapper>
        )}
      </>
    );
  }

  return <></>;
};

export default ProjectRelatedArticle;
